import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { asDirectedHours } from 'components/Bookings/JourneyPlan/timeOptions'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'

const TravelDateForm = ({
  editStates,
  preparedQuote,
  currentBooking,
  quoteRefetch,
  updatePreparedQuote,
  setEditState,
  onDateApplied,
}: any) => {
  const tz =
    preparedQuote?.outbound?.timeZone ||
    currentBooking?.booking.timeZone ||
    'Europe/London'

  const currentTz = DateTime.fromISO(preparedQuote.outbound.when, {
    zone: 'utc',
  }).setZone(tz)
  const nowTz = DateTime.now().setZone(tz)
  const isToday = currentTz.hasSame(nowTz, 'day')

  const step = 5
  const timeOptions = useMemo(
    () =>
      Array.from({ length: (24 * 60) / step }, (_, i) =>
        DateTime.fromObject({ hour: 0, minute: 0 }, { zone: tz })
          .plus({ minutes: i * step })
          .toFormat('HH:mm'),
      ),
    [tz],
  )

  // Controlled input for typing HH:mm
  const [timeInput, setTimeInput] = useState(currentTz.toFormat('HH:mm'))
  useEffect(() => {
    setTimeInput(
      DateTime.fromISO(preparedQuote.outbound.when, { zone: 'utc' })
        .setZone(tz)
        .toFormat('HH:mm'),
    )
  }, [preparedQuote?.outbound.when, tz])

  const commitTime = (raw: string) => {
    const m = raw.trim().match(/^(\d{1,2}):([0-5]\d)$/)
    if (!m) {
      setTimeInput(currentTz.toFormat('HH:mm'))
      return
    }
    let hh = Number(m[1])
    const mm = Number(m[2])
    if (hh < 0 || hh > 23) {
      setTimeInput(currentTz.toFormat('HH:mm'))
      return
    }
    const next = currentTz.set({
      hour: hh,
      minute: mm,
      second: 0,
      millisecond: 0,
    })
    if (isToday && next < nowTz.startOf('minute')) {
      setTimeInput(currentTz.toFormat('HH:mm'))
      return
    }
    setTimeInput(next.toFormat('HH:mm'))
    updatePreparedQuote('outbound', {
      ...preparedQuote.outbound,
      timeZone: tz,
      when: next.setZone('utc').toISO(),
    })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
      }}
    >
      {!editStates.date ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            sx={{
              textWrap: 'balance',
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: editStates.vehicles ? '1.4rem' : '1rem',
            }}
          >
            {DateTime.fromISO(preparedQuote?.outbound.when, { zone: 'utc' })
              .setZone(tz)
              .toFormat('ccc, dd LLLL yyyy - HH:mm')}
            {' | '}
            {(preparedQuote?.outbound.timeZone ?? tz).replaceAll('_', ' ')}
          </Typography>

          {currentBooking?.booking.journeyTypeName === 'AsDirected' && (
            <Typography>
              {asDirectedHours?.map((item) => {
                if (
                  item.value ===
                  String(
                    preparedQuote?.directedJourneyTime ??
                      currentBooking.booking.directedJourneyTime,
                  )
                )
                  return item.label
              })}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale="en-GB"
          >
            <DatePicker
              label="Pick up Date"
              value={DateTime.fromISO(preparedQuote.outbound.when, {
                zone: 'utc',
              }).setZone(tz)}
              minDate={DateTime.now().setZone(tz).startOf('day')}
              inputFormat="dd/MM/yyyy"
              onChange={(dt) => {
                if (!dt || !dt.isValid) return

                const current = DateTime.fromISO(preparedQuote.outbound.when, {
                  zone: 'utc',
                }).setZone(tz)

                const next = current.set({
                  year: dt.year,
                  month: dt.month,
                  day: dt.day,
                })

                updatePreparedQuote('outbound', {
                  ...preparedQuote.outbound,
                  timeZone: tz,
                  when: next.setZone('utc').toISO(),
                })
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ width: 180 }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </LocalizationProvider>

          <Autocomplete
            options={timeOptions}
            freeSolo
            disableClearable
            openOnFocus
            value={currentTz.toFormat('HH:mm')}
            inputValue={timeInput}
            onInputChange={(_, v) => setTimeInput(v)}
            onChange={(_, v) => {
              if (typeof v === 'string') commitTime(v)
            }}
            getOptionDisabled={(opt) => {
              const t = DateTime.fromFormat(opt, 'HH:mm', { zone: tz })
              return isToday && t < nowTz.startOf('minute')
            }}
            ListboxProps={{ style: { maxHeight: 300, overflowY: 'auto' } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pick up Time"
                placeholder="HH:mm"
                onBlur={() => commitTime(timeInput)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commitTime(timeInput)
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  inputMode: 'numeric',
                  pattern: '^[0-2]\\d:[0-5]\\d$',
                }}
                sx={{ width: 160 }}
              />
            )}
          />

          {currentBooking?.booking.journeyTypeName === 'AsDirected' && (
            <FormControl>
              <InputLabel id="as-directed-label">Time / km / miles</InputLabel>
              <Select
                id="as-directed"
                labelId="as-directed-label"
                sx={{ flexGrow: 1, marginRight: '10px' }}
                label="Time / km / miles"
                value={
                  preparedQuote?.directedJourneyTime ||
                  currentBooking?.booking.directedJourneyTime
                }
                defaultValue={currentBooking?.booking.directedJourneyTime}
                onChange={(e) =>
                  updatePreparedQuote(
                    'directedJourneyTime',
                    Number(e.target.value),
                  )
                }
              >
                {asDirectedHours?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            sx={{ display: 'flex', height: '100%' }}
            onClick={() => {
              onDateApplied?.()
              quoteRefetch()
              setEditState('date', false)
            }}
          >
            Apply
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default TravelDateForm
